<template>
    <div class="card">
        <header class="card-header">
            <media-draggable
                class="card-header-title quiz-builder-answer-header"
                :open="multipleChoice && display"
                :disabled="false"
                :can-open="multipleChoice"
                :toggle-callback="() => (display = !display)"
            >
                <template #title>
                    <div v-if="survey && type === 'free_form'">
                        Reference

                        <e-tooltip class="m-l-sm" :label="referenceLabel" multilined position="is-right">
                            <e-icon icon="info-circle" class="has-text-info" />
                        </e-tooltip>
                    </div>

                    <div v-else>
                        <span v-if="multipleChoice"> Answer {{ Number(answer.display_order) + 1 }} </span>

                        <span v-else>{{ body }}</span>
                    </div>
                </template>

                <template #actions>
                    <div v-if="invalid" class="level-item">
                        <auto-save :type="{ global: false, header: false }" :status="validator" />
                    </div>

                    <div v-if="!survey && canAddPointValue" class="level-item tw:mb-7" :class="{ 'is-block': $isMobile }">
                        <e-field
                            :type="{
                                'is-danger': invalidStep,
                                'is-warning': displayPenalty,
                            }"
                            :message="inputMessage"
                            custom-class="p-t-none is-align-center has-text-weight-bold"
                            horizontal
                        >
                            <template #label>
                                <span>Value</span>
                                <span v-if="!survey">*</span>
                            </template>

                            <e-input id="value" v-model="value" type="number" min="0" :step="validStep" />
                        </e-field>
                    </div>

                    <div v-if="multipleChoice" class="level-item">
                        <button
                            :disabled="disabled"
                            :title="disabledText"
                            class="button is-danger is-outlined"
                            @click.prevent="remove"
                            @keyup.prevent="remove"
                        >
                            <e-icon icon="trash-alt" />
                        </button>
                    </div>
                </template>
            </media-draggable>
        </header>

        <div v-if="multipleChoice && display" class="card-content">
            <div class="columns">
                <div class="column">
                    <e-field :label="bodyLabel">
                        <rich-text-editor
                            v-if="isEditorReady"
                            v-model="body"
                            enable-wrapper
                            :placeholder="exampleReference"
                            @error="handleEditorError"
                        />
                        <e-input v-else v-model="body" type="textarea" :placeholder="exampleReference" />
                    </e-field>
                </div>
            </div>

            <div v-if="!survey" class="columns">
                <div class="column">
                    <e-field label="Rationale">
                        <e-input v-model="rationale" type="textarea" placeholder="Type rationale here..." />
                    </e-field>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import has from 'lodash/has.js';
import isEmpty from 'lodash/isEmpty.js';
import some from 'lodash/some.js';

import { useI18n } from 'vue-i18n';

import AutoSave from '@/components/auto-save/AutoSave.vue';
// Import Editor component lazily
import Editor from '@/components/editor/Editor.vue';
import MediaDraggable from '@/components/elements/MediaDraggable.vue';
import QuizTypeMixin from '@/mixins/QuizType.mixin.js';

const field = name => {
    return {
        get() {
            return this.getField(name);
        },
        set(value) {
            this.set(name, value);
        },
    };
};

export default {
    name: 'QuizAnswer',

    components: {
        AutoSave,
        RichTextEditor: Editor,
        MediaDraggable,
    },

    mixins: [QuizTypeMixin],

    props: {
        answer: {
            type: Object,
            required: true,
        },

        type: {
            type: String,
            required: true,
        },

        uuid: {
            type: [String, Number],
            required: true,
        },
    },

    data: () => ({
        display: false,
        validStep: 0.25,
        isEditorReady: false,
    }),

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    computed: {
        rationale: field('rationale'),

        value: field('value'),

        body: field('body'),

        answers() {
            return this.$store.getters[`QuizBuilder/${this.uuid}/question`]({
                order: this.answer.parent,
                field: 'answers',
            }).length;
        },

        hasPartialCreditEnabled() {
            return this.$store.getters[`QuizBuilder/${this.uuid}/question`]({
                order: this.answer.parent,
                field: 'partial_credit',
            });
        },

        chevron() {
            return this.display ? 'angle-up' : 'angle-down';
        },

        disabled() {
            return this.answers <= 1;
        },

        disabledText() {
            return this.disabled ? 'Minimum Two Answers Required' : 'Delete Answer';
        },

        invalid() {
            return some(this.validator, item => !isEmpty(item));
        },

        multipleChoice() {
            return this.type.includes('multiple') || this.type === 'free_form';
        },

        multipleAnswer() {
            return this.type === 'multiple_choice_multiple_answer';
        },

        trueFalse() {
            return this.type === 'true_false';
        },

        bodyLabel() {
            if (this.survey) {
                if (this.type === 'free_form') return '';

                return 'Answer Body';
            }

            return this.type === 'free_form' ? '' : 'Answer Body *';
        },

        referenceLabel() {
            return 'If the survey is going to be graded, an optional reference can be attached to this question during the grading process.';
        },

        exampleReference() {
            if (this.survey && this.type === 'free_form') {
                return 'Example: Response should include specific comments about the course experience...';
            }

            return 'Type answer here...';
        },

        validator() {
            const validator = this.$store.getters[`QuizBuilder/${this.uuid}/validate`]({
                target: this.answer.parent,
                type: 'answers',
            }).answers;

            if (has(validator, 'errors') && !isEmpty(validator.errors)) {
                const index = this.getIndex(validator.errors);

                if (index >= 0) return validator.errors[this.getIndex(validator.errors)].errors;
            }

            return { body: null, value: null };
        },

        invalidStep() {
            return this.value % this.validStep !== 0;
        },

        inputMessage() {
            if (this.invalidStep) {
                return this.translate('multiples_of_are_accepted', {
                    step: this.validStep,
                });
            }

            if (this.hasPartialCreditEnabled && this.value === 0) {
                return this.translate('recommended_value');
            }

            return '';
        },

        displayPenalty() {
            return this.hasPartialCreditEnabled && this.value === 0;
        },

        canAddPointValue() {
            if (this.trueFalse || this.type === 'multiple_choice') return true;

            return this.multipleAnswer && this.hasPartialCreditEnabled;
        },
    },

    mounted() {
        // Initialize editor after component is mounted
        this.$nextTick(() => {
            this.isEditorReady = true;
        });
    },

    created() {
        if (this.survey && this.type === 'free_form') this.display = true;
    },

    methods: {
        handleEditorError(error) {
            console.error('Editor error:', error);
            this.isEditorReady = false;
            // You can also report this to Sentry here
            this.$captureError(error, {
                component: 'QuizAnswer',
                action: 'editorError',
            });
        },

        getField(name) {
            return this.$store.getters[`QuizBuilder/${this.uuid}/answer`]({
                field: name,
                id: this.answer.id,
                order: Number(this.answer.display_order),
                parent: this.answer.parent,
            });
        },

        getIndex(payload) {
            return payload.findIndex(item => item.display_order === this.answer.display_order);
        },

        remove() {
            this.$store.dispatch(`QuizBuilder/${this.uuid}/confirmRemoval`, {
                parent: this.answer.parent,
                target: this.answer.display_order,
                type: 'answers',
                store: this.$store,
            });
        },

        set(name, value) {
            this.$store.dispatch(`QuizBuilder/${this.uuid}/setAnswerField`, {
                field: name,
                order: this.answer.display_order,
                parent: this.answer.parent,
                value,
            });
        },
    },
};
</script>

<style lang="scss">
.quiz-builder-answer-header > .level > .level-item.media-draggable__title {
    overflow: visible !important;
}
</style>
